import DefaultMap from '@/utils/DefaultMap'
import isServerOffline from '@/utils/isServerOffline'
import { compareApps } from '@/utils/sortApps'
import { FRONTEND_APP_TYPE } from '@/config/appTypes'

function optional(type, value, default_) {
  if (value === undefined) value = default_
  if (!value && value !== 0) return false
  // eslint-disable-next-line valid-typeof
  if (typeof value !== type) throw new Error(type + ' argument expected')
  return value
}

class Selection {
  constructor(cache, query) {
    this._cache = cache
    this._query = query
  }

  get processes() {
    let processes = this._cache.processes
    const { appName, serverName, pmId, onlineOnly } = this._query

    if (appName) {
      processes = this._cache.processesByApp.get(appName) || []
    }

    if (pmId !== false) {
      processes = processes.filter(p => p.pm_id === pmId)
    }

    if (serverName) {
      processes = processes.filter(p => p.server === serverName)
    }

    if (onlineOnly) {
      processes = processes.filter(p => !isServerOffline(p.server))
    }

    return processes
  }

  get servers() {
    let servers = this._cache.servers
    const { appName, serverName, pmId, onlineOnly } = this._query

    if (appName || pmId !== false) {
      // process filter
      const names = new Set(this.processes.map(p => p.server))
      servers = servers.filter(server => names.has(server.server_name))
    } else {
      if (onlineOnly) {
        servers = servers.filter(server => !isServerOffline(server.server_name))
      }

      if (serverName) {
        servers = servers.filter(server => server.server_name === serverName)
      }
    }

    return servers
  }

  get appNames() {
    const { appName, serverName, pmId, onlineOnly } = this._query

    const result = new Set(this.processes.map(p => p.name))
    // Add browser applications if no specific filter on processes is given
    if (!serverName && pmId === false && !onlineOnly) {
      if (appName) {
        if (this._cache.applications.some(application => application.name === appName)) {
          result.add(appName)
        }
      } else {
        this._cache.applications.forEach(application => result.add(application.name))
      }
    }

    return Array.from(result)
  }

  get sortedAppNames() {
    return this.appNames.sort((a, b) => {
      return compareApps(
        {
          name: a,
          processes: this._cache.processesByApp.get(a),
        },
        {
          name: b,
          processes: this._cache.processesByApp.get(b),
        },
      )
    })
  }
}

export default {
  getters: {
    select: (state, getters) => {
      const currentBucket = getters.currentBucket
      const appType = getters.currentAppType
      let cache
      if (!currentBucket || !appType) {
        cache = {
          processes: [],
          servers: [],
          applications: [],
          processesByApp: new Map(),
        }
      } else if (appType === FRONTEND_APP_TYPE) {
        cache = {
          processes: [],
          servers: [],
          applications: getters.frontendApps,
          processesByApp: new Map(),
        }
      } else {
        const processesByApp = new DefaultMap(() => [])
        currentBucket.processes.forEach(p => processesByApp.get(p.name).push(p))
        cache = {
          processes: currentBucket.processes,
          servers: currentBucket.servers,
          applications: [],
          processesByApp,
        }
      }

      return query => {
        if (!query) query = {}
        return new Selection(cache, {
          appName: optional(
            'string',
            query.appName,
            getters.currentRouteRequireApp && getters.currentAppName,
          ),
          serverName: optional('string', query.serverName),
          pmId: optional('number', query.pmId),
          onlineOnly: optional('boolean', query.onlineOnly),
        })
      }
    },
  },
}
